import React, { useState, useRef, useEffect } from 'react';
import { Box } from '@mui/material';

interface MiniDoubleSliderProps {
  min: number;
  max: number;
  rangeMin: number;
  rangeMax: number;
  onChange: (min: number, max: number) => void;
  step?: number;
  width?: number;
  height?: number;
  trackColor?: string;
  rangeColor?: string;
  handleColor?: string;
  disabled?: boolean;
  currentValue?: number;
  currentValueColor?: string;
}

const MiniDoubleSlider: React.FC<MiniDoubleSliderProps> = ({
  min,
  max,
  rangeMin,
  rangeMax,
  onChange,
  step = 1,
  width = 100,
  height = 6,
  trackColor = '#e0e0e0',
  rangeColor = 'rgba(76, 175, 80, 0.3)',
  handleColor = '#4CAF50',
  disabled = false,
  currentValue,
  currentValueColor = '#FF5722',
}) => {
  const [dragging, setDragging] = useState<'min' | 'max' | null>(null);
  const [tempRangeMin, setTempRangeMin] = useState<number>(rangeMin);
  const [tempRangeMax, setTempRangeMax] = useState<number>(rangeMax);
  const containerRef = useRef<HTMLDivElement>(null);

  // Convert value to pixel position
  const valueToPixel = (value: number): number => {
    const percentage = (value - min) / (max - min);
    return percentage * width;
  };

  // Convert pixel position to value
  const pixelToValue = (pixel: number): number => {
    const percentage = pixel / width;
    const value = min + percentage * (max - min);
    // Round to step
    return Math.round(value / step) * step;
  };

  // Sync temp values with props when not dragging
  useEffect(() => {
    if (!dragging) {
      setTempRangeMin(rangeMin);
      setTempRangeMax(rangeMax);
    }
  }, [rangeMin, rangeMax, dragging]);

  const handleMouseDown = (type: 'min' | 'max') => (e: React.MouseEvent) => {
    if (disabled) return;
    e.preventDefault();
    setDragging(type);
  };

  useEffect(() => {
    if (!dragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const clampedX = Math.max(0, Math.min(width, x));
      const newValue = pixelToValue(clampedX);

      if (dragging === 'min') {
        const clampedValue = Math.max(min, Math.min(newValue, tempRangeMax));
        if (clampedValue !== tempRangeMin) {
          setTempRangeMin(clampedValue);
        }
      } else {
        const clampedValue = Math.max(tempRangeMin, Math.min(newValue, max));
        if (clampedValue !== tempRangeMax) {
          setTempRangeMax(clampedValue);
        }
      }
    };

    const handleMouseUp = () => {
      // Call onChange only on drop
      if (tempRangeMin !== rangeMin || tempRangeMax !== rangeMax) {
        onChange(tempRangeMin, tempRangeMax);
      }
      setDragging(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, tempRangeMin, tempRangeMax, rangeMin, rangeMax, min, max, width, onChange, step]);

  // Use temp values during drag, actual values otherwise
  const displayMin = dragging ? tempRangeMin : rangeMin;
  const displayMax = dragging ? tempRangeMax : rangeMax;

  const minPosition = valueToPixel(displayMin);
  const maxPosition = valueToPixel(displayMax);
  const handleSize = 12;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: `${width}px`,
        height: `${height + handleSize}px`,
        display: 'flex',
        alignItems: 'center',
        cursor: disabled ? 'default' : 'pointer',
        userSelect: 'none',
      }}
    >
      {/* Track */}
      <Box
        sx={{
          position: 'absolute',
          top: `${handleSize / 2}px`,
          left: 0,
          width: `${width}px`,
          height: `${height}px`,
          backgroundColor: trackColor,
          borderRadius: `${height / 2}px`,
        }}
      />

      {/* Range highlight */}
      <Box
        sx={{
          position: 'absolute',
          top: `${handleSize / 2}px`,
          left: `${minPosition}px`,
          width: `${maxPosition - minPosition}px`,
          height: `${height}px`,
          backgroundColor: rangeColor,
          borderRadius: `${height / 2}px`,
        }}
      />

      {/* Min handle */}
      <Box
        onMouseDown={handleMouseDown('min')}
        sx={{
          position: 'absolute',
          left: `${minPosition - handleSize / 2}px`,
          top: `${(handleSize / 2 + height / 2) - handleSize / 2}px`,
          width: `${handleSize}px`,
          height: `${handleSize}px`,
          backgroundColor: handleColor,
          border: '2px solid white',
          borderRadius: '50%',
          cursor: disabled ? 'default' : 'ew-resize',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          transition: dragging === 'min' ? 'none' : 'left 0.1s',
          '&:hover': {
            transform: disabled ? 'none' : 'scale(1.2)',
          },
        }}
      />

      {/* Max handle */}
      <Box
        onMouseDown={handleMouseDown('max')}
        sx={{
          position: 'absolute',
          left: `${maxPosition - handleSize / 2}px`,
          top: `${(handleSize / 2 + height / 2) - handleSize / 2}px`,
          width: `${handleSize}px`,
          height: `${handleSize}px`,
          backgroundColor: handleColor,
          border: '2px solid white',
          borderRadius: '50%',
          cursor: disabled ? 'default' : 'ew-resize',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          transition: dragging === 'max' ? 'none' : 'left 0.1s',
          '&:hover': {
            transform: disabled ? 'none' : 'scale(1.2)',
          },
        }}
      />

      {/* Current value marker (triangle) */}
      {currentValue !== undefined && currentValue >= min && currentValue <= max && (
        <Box
          sx={{
            position: 'absolute',
            left: `${valueToPixel(currentValue)}px`,
            top: '-2px',
            width: 0,
            height: 0,
            borderLeft: '4px solid transparent',
            borderRight: '4px solid transparent',
            borderTop: `6px solid ${currentValueColor}`,
            transform: 'translateX(-4px)',
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  );
};

export default MiniDoubleSlider;
